import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ImageIcon } from 'lucide-react'
import { apiClient } from '@/lib/api/client'

export type Preview = { scene: string; url?: string; unavailable?: boolean }
export type Recipe = {
  title: string
  artist?: string
  durationSec: number
  preamble?: unknown
  shots: unknown[]
}
export type Template = {
  id: string
  title: string
  format: string
  identityGaps: string[]
  recipe: Recipe
  previews?: Preview[]
}
export const studioButton =
  'min-h-11 rounded-lg border px-4 py-2 text-sm font-medium transition hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50'
export const humanize = (s: string) =>
  s
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase())

export function StudioStatus({ status }: { status: string }) {
  const color = ['approved', 'release_review'].includes(status)
    ? 'bg-emerald-100 text-emerald-900'
    : ['failed', 'cancelled'].includes(status)
      ? 'bg-red-100 text-red-900'
      : ['review_pending', 'revision_requested'].includes(status)
        ? 'bg-amber-100 text-amber-900'
        : 'bg-muted text-muted-foreground'
  return (
    <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium ${color}`}>
      {humanize(status)}
    </span>
  )
}

export function StudioKpi({
  label,
  value,
  note,
}: {
  label: string
  value?: number
  note?: string
}) {
  return (
    <div className="min-w-0 rounded-xl border bg-card p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-3xl font-semibold tabular-nums">
        {value === undefined ? '—' : value}
      </p>
      {note && <p className="mt-1 text-xs text-muted-foreground">{note}</p>}
    </div>
  )
}

export function ArtworkPreview({
  title,
  previews,
  jobId,
  sourceUrl,
}: {
  title: string
  previews?: Preview[]
  jobId?: string
  sourceUrl?: string
}) {
  const [failed, setFailed] = useState('')
  const [thumbnailUrl, setThumbnailUrl] = useState('')
  const canonical = sourceUrl?.match(
    /^https:\/\/picasso\.arthaus\.cloud\/cache\/artworks\/(\d{1,10}-[a-z0-9-]{1,150})\.webp$/
  )?.[1]
  const thumbnail = useQuery({
    queryKey: ['studio-thumbnail', canonical],
    queryFn: async () =>
      (await apiClient.get<Blob>(`/api/v1/worlds/thumbnail/${canonical}`, { responseType: 'blob' }))
        .data,
    enabled: !!canonical,
    staleTime: 900000,
    retry: false,
  })
  useEffect(() => {
    setThumbnailUrl('')
    if (!canonical || !(thumbnail.data instanceof Blob) || !URL.createObjectURL) return
    const url = URL.createObjectURL(thumbnail.data)
    setThumbnailUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [canonical, thumbnail.data])
  const query = useQuery({
    queryKey: ['film-preview', jobId],
    queryFn: async () =>
      (await apiClient.get<{ previews: Preview[] }>(`/api/v1/films/jobs/${jobId}/previews`)).data
        .previews,
    enabled: !!jobId,
    staleTime: 480000,
    refetchInterval: 480000,
    retry: false,
  })
  const url =
    (canonical ? thumbnailUrl : sourceUrl) ||
    (!canonical && (jobId ? query.data : previews)?.find((p) => p.url)?.url)
  return (
    <div className="relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-lg bg-muted">
      {url && failed !== url ? (
        <img
          alt={title}
          src={url}
          loading="lazy"
          className="h-full w-full object-contain"
          onError={() => setFailed(url)}
        />
      ) : (
        <div className="flex flex-col items-center gap-2 p-4 text-center text-xs text-muted-foreground">
          <ImageIcon aria-hidden="true" className="h-6 w-6" />
          <span>{query.isFetching ? 'Loading artwork…' : 'Artwork preview unavailable'}</span>
        </div>
      )}
    </div>
  )
}
